import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import AutocompleteInput from '../AutocompleteInput';
import axios from '../../lib/axios';
import Filter from './Filter';

const KEY = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  UP: 38,
  DOWN: 40,
};

export default class AutocompleteFilter extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    renderItem: PropTypes.func,
    options: PropTypes.array,
    selected: PropTypes.array,
    onChange: PropTypes.func,
    requestThrottle: PropTypes.number,
  }

  static defaultProps = {
    url: null,
    options: [],
    selected: [],
    renderItem: null,
    onChange() {},
    requestThrottle: 250,
  }

  constructor(props) {
    super(props);

    this.requestCancelToken = axios.CancelToken.source();
    this.state = {
      open: false,
      selected: this.props.selected,
      selectedIndex: 0,
      results: this.props.options.length > 0 ? this.props.options : [],
    };

    this.autocompleteRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleInputClick = () => {
    this.clickHandled = true;
    this.setState({ open: true });
  }

  handleClickOutside = () => {
    if (!this.clickHandled) {
      this.setState({ open: false });
    } else {
      this.clickHandled = false;
    }
  }

  handleLocalResetClick = () => {
    const selected = [];
    this.setState({ selected, selectedIndex: 0 }, () => {
      this.syncPushState(selected);
      this.props.onChange('selected', { id: this.props.id, selected });
      this.autocompleteRef.value = '';
    });
  }

  handleGlobalResetClick = () => {
    const selected = [];
    this.setState({ selected, selectedIndex: 0 }, () => {
      this.props.onChange('selected', { id: this.props.id, selected });
      this.autocompleteRef.value = '';
    });
  }

  handleInputChange = (e) => {
    const query = e.target.value;
    const { options, url } = this.props;

    if (options.length === 0) {
      this.handleLoad(query, url);
    } else {
      const filteredResults = options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));
      this.setState({ results: filteredResults });
    }
  }

  handleLoad = (query, url) => {
    if (this.request) {
      this.requestCancelToken.cancel();
    }

    if (!query) {
      return this.setState({ results: [] });
    }

    clearTimeout(this.requestThrottle);
    this.requestThrottle = setTimeout(
      () => this.performRequest(url, { query }),
      this.props.requestThrottle
    );
  }

  performRequest = (urls, params) => {
    this.requestCancelToken = axios.CancelToken.source()

    this.request = (Array.isArray(urls) ? urls : [urls]).map((url) => (
      axios.get(url, {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets', depth: 2 }),
        cancelToken: this.requestCancelToken.token
      })
    ));

    axios.all(this.request)
      .then((...args) => {
        const allResults = [].concat(args[0].flatMap((r) => r.data));
        const uniqueKeys = new Set();
        const results = allResults.filter((r) => {
          const id = r.slug || r.id;
          if (!uniqueKeys.has(id)) {
            uniqueKeys.add(id);
            return true;
          }
          return false;
        });
        this.setState({ results });
      });
  }

  handleResultClick = (index) => {
    const { selected } = this.state;

    const item = {
      id: this.state.results[index].slug || this.state.results[index].id,
      name: this.state.results[index].name || this.state.results[index].value || this.state.results[index].name_with_path,
    };

    selected.push(item);

    this.setState({ selected, open: false, selectedIndex: 0 }, () => {
      this.syncPushState(selected);
      this.props.onChange('selected', { id: this.props.id, selected });
    });
  }

  handleKeyDown = (e) => {
    if (!this.state.open) return;

    switch (e.keyCode) {
      case KEY.ESC:
        this.setState({ open: false });
        break;
      case KEY.UP:
        e.preventDefault();
        this.shiftSelectedIndex(-1);
        break;
      case KEY.DOWN:
        e.preventDefault();
        this.shiftSelectedIndex(+1);
        break;
      case KEY.TAB:
      case KEY.RETURN: {
        e.preventDefault();
        this.handleResultClick(this.state.selectedIndex);
        break;
      }
      default:
    }
  }

  handleOptionMouseEnter = (selectedIndex) => {
    this.setState({ selectedIndex });
  }

  shiftSelectedIndex = (delta) => {
    const count = this.state.results.length;
    this.setState({
      selectedIndex: (count + this.state.selectedIndex + delta) % count,
    });
  };

  handleRemoveSelectedClick = (item) => {
    const index = this.state.selected.indexOf(item);
    const selected = this.state.selected.slice(0, index)
      .concat(this.state.selected.slice(index + 1)); // Remove without mutating

    this.setState({ selected }, () => {
      this.syncPushState(selected);
      this.props.onChange('selected', { id: this.props.id, selected });
    });
  }

  syncPushState = (selected) => {
    const params = qs.parse(window.location.search.slice(1));
    const newValues = selected.map((item) => item.id);
    const oldValues = params[this.props.id] || newValues;
    const removedType = newValues.reduce((ok, v) => ok && oldValues.indexOf(v) !== -1, true);

    params[this.props.id] = newValues;
    router.push({
      pathname: location.pathname,
      search: qs.stringify(params, { arrayFormat: 'brackets' }),
      state: { revisitWithSearch: true, removedType },
    });
  }

  renderItem(item) {
    return this.props.renderItem ? this.props.renderItem(item) : item.name;
  }

  renderResults() {
    return (
      <div className="u-border u-rounded u-shadow u-bg-white u-absolute u-inset-x-0 u-top-0 u-mt-10 u-z-10 u-max-h-xs u-overflow-auto">
        { this.state.results.length === 0 && (
          <div className="u-px-2 u-py-1 u-text-sm">
            No results found
          </div>
        )}
        { this.state.results.length > 0
          && (
          <ul className="u-m-0">
            { this.state.results.map((option, index) => {
              const item = {
                id: option.slug || option.id,
                name: option.name || option.value || option.name_with_path,
              };

              return (
                <li
                  className={`u-px-2 u-py-1 u-text-sm u-cursor-pointer ${index === this.state.selectedIndex ? 'u-bg-old-primary-color-blue-mega-dim' : ''}`}
                  key={item.id}
                  onClick={() => this.handleResultClick(index)}
                  onMouseEnter={() => this.handleOptionMouseEnter(index)}
                  data-item-value={item.id}
                  data-item-label={item.name}
                >
                  {this.renderItem(option)}
                </li>
              );
            })}
          </ul>
          )}
      </div>
    );
  }

  render() {
    const {
      id, title, expanded, onChange,
    } = this.props;
    return (
      <Filter id={id} title={title} expanded={expanded} hasSelected={this.state.selected.length > 0} onChange={onChange} onLocalReset={this.handleLocalResetClick} onGlobalReset={this.handleGlobalResetClick}>
        <div className="Form u-px-4 u-pb-2">
          <div className="u-relative">
            <AutocompleteInput
              id={`${this.props.id}-autocomplete`}
              onKeyDown={this.handleKeyDown}
              onChange={this.handleInputChange}
              onClick={this.handleInputClick}
              placeholder={this.props.options.length === 0 ? "Type to see suggestions" : "Type to filter suggestions"}
              onClickSelected={this.handleRemoveSelectedClick}
              innerRef={(node) => this.autocompleteRef = node}
              selected={this.state.selected}
            >
              { this.state.open && this.renderResults() }
            </AutocompleteInput>
            { this.props.options.length > 0 && !this.state.open && (
              <i className="Icon Icon-chevron-down u-absolute u-right-0 u-mr-1 u-text-grey-dark u-top-0 u-pt-2 u-flex u-items-center"></i>
            )}
          </div>
        </div>
      </Filter>
    );
  }
}
